// The nav-bar entry point for the DEV/LOCAL fixture switcher. Renders NOTHING outside a non-production lane,
// on native, or for a non-fixture account — so it is absent from every real session by construction rather
// than by a build flag someone has to remember.
//
// The BUTTON only. The dialog is mounted by the shell at ROOT level, not here, because `Modal` renders in
// place rather than through a portal and the topbar is a stacking context (`position: sticky` + a z-index,
// plus `backdrop-filter`, either of which alone is enough). A dialog rendered from inside the actions row is
// therefore trapped in the nav bar — scrim and all — which is exactly what happened. Every other modal in
// these shells mounts beside `<main>` for the same reason; this follows that precedent instead of reaching
// for a portal the codebase does not otherwise use.
import { UserRoundCog } from "lucide-react";
import { IconButton } from "@cntrl/ui";
import { useCanSwitchFixtureAccounts, useActingAs } from "../../app/hooks/useDevFixtureSwitch.js";
import { useFixtureSwitchVisibility } from "../../app/fixtureSwitchVisibility.js";
import styles from "./FixtureSwitchButton.module.scss";

export function FixtureSwitchButton({ open, onOpen }: { open: boolean; onOpen: () => void }) {
  const canSwitch = useCanSwitchFixtureAccounts();
  const actingAs = useActingAs();
  // Settings › Account can hide it for screenshots (dev only; see app/fixtureSwitchVisibility.ts).
  const hidden = useFixtureSwitchVisibility((s) => s.hidden);

  if (!canSwitch || hidden) return null;

  return (
    <IconButton
      icon={UserRoundCog}
      // Names WHO you are when swapped, because that is the question a swapped session makes ambiguous —
      // and this is the one control on screen that can answer it from anywhere in the app.
      label={actingAs ? `View as — acting as ${actingAs.displayName}` : "View as (dev)"}
      // A swapped session is marked HERE, and only here (2026-09-01, replacing the standing banner). Every
      // other surface looks exactly as it would for the real person — that is the point of minting a real
      // session — so the marker has to live on something always on screen. The glow is the whole tell, so
      // it must never be the only tell for assistive tech: the accessible name above carries the same fact.
      className={actingAs ? styles.acting : undefined}
      tone="outline"
      iconSize={16}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onOpen}
    />
  );
}
